import { z } from 'zod';

import { USER_DESTINATIONS } from '../constants/index.js';
import {
  SALES_COVERAGE_REQUIRED_MESSAGE,
  isSalesRole,
  missingSalesCoverage,
} from '../constants/sales-departments.js';

import { DateOnlySchema } from './agent-targets.js';
import { UserRoleSchema } from './auth.js';
import { EmailSchema, IsoDateTimeSchema, UuidSchema, BooleanQueryParamSchema } from './common.js';
import { OpsDepartmentSchema } from './ops-department.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * User shapes for the admin users module.
 *
 * - `UserListItem` is the row shape returned by `GET /users` (lighter, joins
 *   only show display labels).
 * - `UserDetail` is the single-record shape from `GET /users/:id` (adds soft
 *   delete + must-reset + role assignments).
 * - `CreateUserRequest` is what an admin posts to provision a new user. We
 *   never accept a password here: the server generates one and emails a reset
 *   link, mirroring the cutover flow for ETL-imported users.
 * - `UpdateUserRequest` is partial - admins patch only what they want to change.
 */

const RoleRefSchema = z.object({
  id: UuidSchema,
  code: z.string(),
  name: z.string(),
});

const NamedRefSchema = z.object({
  id: UuidSchema,
  name: z.string(),
});

const UserNamedRefSchema = z.object({
  id: UuidSchema,
  fullName: z.string(),
});

// Sales-team dimensions had two lives:
//   - 2026-06-22 moved them off the user onto the Destination master,
//     so the user's segmentation was DERIVED from destinations.
//   - 2026-06-26 ADR-0057 reversed that: scope[], haul[], tripTypes[]
//     are now FIRST-CLASS multi-select arrays on the user, declared
//     INDEPENDENTLY of the destinations. The destination picks
//     themselves became ordered (1 primary + up to 2 optional).
//
// Why reverse: operators wanted "I cover Bali but only FIT" without
// re-tagging Bali in the destination master, and the derived model
// couldn't express "I cover INTL + DOMESTIC at the same time" when
// the picked destinations disagreed on classification.

/** Per-user sales scope (multi). Empty array = "not classified". */
export const UserSalesScopeSchema = z.enum(['DOMESTIC', 'INTERNATIONAL']);
export type UserSalesScope = z.infer<typeof UserSalesScopeSchema>;

/** Per-user sales haul (multi). Empty array = "not classified". */
export const UserSalesHaulSchema = z.enum(['SHORT_HAUL', 'LONG_HAUL']);
export type UserSalesHaul = z.infer<typeof UserSalesHaulSchema>;

/**
 * One destination in the user's ordered coverage list. `priority`
 * encodes ordering: 1 = primary (required on create for sales roles),
 * 2+ = additional destinations added via the admin form. The server
 * stores these in `user_destinations.priority` with a UNIQUE
 * (user_id, priority) so priorities never collide.
 */
export const UserDestinationSlotSchema = z.object({
  destinationId: UuidSchema,
  priority: z.number().int().min(USER_DESTINATIONS.MIN_PRIORITY).max(USER_DESTINATIONS.MAX_COUNT),
  // `dailyLeadCapacity` (the old per-destination "leads/day" cap) was retired
  // 2026-10-28. Non-LPS leads no longer carry a cap; only marketing (reporting)
  // and LPS (gate + cap) remain. The DB column is kept but no longer written.
  /// Marketing leads per day for this destination. Null / omitted = not set.
  marketingLeadCapacity: z
    .number()
    .int()
    .min(1)
    .max(USER_DESTINATIONS.MAX_DAILY_LEAD_CAPACITY)
    .nullable()
    .optional(),
  /// LPS leads per day for this destination: when set, auto-assignment deals
  /// this user at most that many LPS leads for it per day (own counter).
  /// Null / omitted = this user gets NO LPS leads for this destination.
  lpsLeadCapacity: z
    .number()
    .int()
    .min(1)
    .max(USER_DESTINATIONS.MAX_DAILY_LEAD_CAPACITY)
    .nullable()
    .optional(),
});
export type UserDestinationSlot = z.infer<typeof UserDestinationSlotSchema>;

/**
 * Display-friendly slot returned in `UserListItem` / `UserDetail`.
 * Carries the destination's name + priority so the admin grid can
 * render "Goa (primary)" without a second round-trip.
 */
const UserDestinationSlotRefSchema = z.object({
  id: UuidSchema,
  name: z.string(),
  priority: z.number().int().min(USER_DESTINATIONS.MIN_PRIORITY).max(USER_DESTINATIONS.MAX_COUNT),
  marketingLeadCapacity: z.number().int().nullable().optional(),
  lpsLeadCapacity: z.number().int().nullable().optional(),
});

export const UserListItemSchema = z.object({
  id: UuidSchema.describe('UUID v7 of the user'),
  email: EmailSchema.describe('Login email, lowercased + unique'),
  fullName: z.string().describe('Display name'),
  phone: z.string().nullable().describe('Optional phone number'),
  callerDeskNumber: z
    .string()
    .nullable()
    .describe('CallerDesk member/virtual number used for click-to-call + alt login'),
  employeeCode: z.string().nullable().describe('Optional HR / employee identifier'),
  dateOfBirth: DateOnlySchema.nullable().describe('Date of birth (YYYY-MM-DD)'),
  dateOfJoining: DateOnlySchema.nullable().describe('Date of joining (YYYY-MM-DD)'),
  dateOfExit: DateOnlySchema.nullable().describe('Date of exit (YYYY-MM-DD)'),
  isAdmin: z.boolean().describe('Administrator flag (HR / access metadata)'),
  role: UserRoleSchema,
  branch: NamedRefSchema.nullable().describe('Branch the user belongs to'),
  department: NamedRefSchema.nullable().describe('Department within the branch'),
  subBranch: NamedRefSchema.nullable().describe('Sub-branch the user belongs to'),
  reportingManager: UserNamedRefSchema.nullable().describe('Immediate reporting manager'),
  isActive: z.boolean().describe('False blocks login and hides the user from default listings'),
  mustResetPassword: z
    .boolean()
    .describe('True for ETL-imported or newly-created users until they complete a reset'),
  autoAssignLeads: z
    .boolean()
    .describe('False keeps Lead Parsing System leads away from this user unless assigned by hand'),
  opsDepartment: OpsDepartmentSchema.nullable().describe(
    'Operations desk (Flight / DMC-Hotel / Visa). Null = not in any ops rotation.',
  ),
  // 2026-06-26 ADR-0057. First-class sales coverage dimensions.
  // Empty arrays = "not classified yet" - matches the pre-migration
  // state for legacy rows.
  salesScopes: z.array(UserSalesScopeSchema),
  salesHauls: z.array(UserSalesHaulSchema),
  salesTripTypes: z.array(NamedRefSchema).describe('Trip types this user covers (TripType master)'),
  // 2026-06-27: `destinationCity` retired. The ordered `destinations`
  // slots below subsume it.
  /// Ordered destination coverage. Empty array = no destinations
  /// picked yet. When non-empty, exactly one row has priority=1
  /// (primary); priorities 2 + 3 are optional.
  destinations: z
    .array(UserDestinationSlotRefSchema)
    .describe('Ordered destinations this user covers (1=primary, 2/3=secondary/tertiary)'),
  lastLogin: IsoDateTimeSchema.nullable().describe('ISO timestamp of the last successful login'),
  createdAt: IsoDateTimeSchema.describe('ISO timestamp the user was created'),
});
export type UserListItem = z.infer<typeof UserListItemSchema>;

export const UserDetailSchema = UserListItemSchema.extend({
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
  assignedRoles: z.array(RoleRefSchema),
});
export type UserDetail = z.infer<typeof UserDetailSchema>;

export const UserListQuerySchema = PaginationQuerySchema.extend({
  role: UserRoleSchema.optional(),
  /** Multi-select: comma-separated roles → array. Takes precedence over `role`. */
  roles: z
    .preprocess(
      (v) =>
        typeof v === 'string'
          ? v
              .split(',')
              .map((s) => s.trim())
              .filter(Boolean)
          : v,
      z.array(UserRoleSchema).min(1).max(20),
    )
    .optional(),
  branchId: UuidSchema.optional(),
  departmentId: UuidSchema.optional(),
  subBranchId: UuidSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type UserListQuery = z.infer<typeof UserListQuerySchema>;

export const UserListResponseSchema = paginatedResponseSchema(UserListItemSchema);
export type UserListResponse = z.infer<typeof UserListResponseSchema>;

/**
 * Slim row returned by `GET /users/assignable-agents` (ADR-0039 lead
 * module). Used to populate the "Assigned to" + "Created by" pickers on
 * the lead form. Scoped server-side by the caller's role:
 *   - ADMIN          -> every active SALES_LEAD + SALES_AGENT
 *   - SALES_LEAD     -> self + direct reports
 *   - SALES_AGENT    -> self only
 */
export const AssignableAgentSchema = z.object({
  id: UuidSchema,
  fullName: z.string(),
  role: UserRoleSchema,
  branchId: UuidSchema.nullable(),
});
export type AssignableAgent = z.infer<typeof AssignableAgentSchema>;

export const AssignableAgentListSchema = z.array(AssignableAgentSchema);
export type AssignableAgentList = z.infer<typeof AssignableAgentListSchema>;

/**
 * Slim row returned by `GET /users/eligible-managers?role=X`. Lists
 * active users whose role is a valid parent for the supplied role per
 * the ROLE_HIERARCHY map (e.g. ?role=SALES_AGENT returns SALES_LEAD
 * users). Drives the dependent "reporting manager" dropdown on the
 * Add/Edit User form.
 */
export const EligibleManagerSchema = z.object({
  id: UuidSchema,
  fullName: z.string(),
  email: EmailSchema,
  role: UserRoleSchema,
  branch: z
    .object({
      id: UuidSchema,
      name: z.string(),
    })
    .nullable(),
});
export type EligibleManager = z.infer<typeof EligibleManagerSchema>;
export const EligibleManagerListSchema = z.array(EligibleManagerSchema);
export type EligibleManagerList = z.infer<typeof EligibleManagerListSchema>;

/**
 * Node in the org chart returned by `GET /users/:id/hierarchy`.
 *
 * The server caps depth at two report levels (direct reports + each
 * one's direct reports). Three concentric schemas keep the structure
 * fully typed without resorting to z.lazy:
 *   HierarchyLeafSchema  - bare user row, no reports
 *   HierarchyMidSchema   - leaf + reports: HierarchyLeaf[]
 *   HierarchyUserNode    - mid + reports: HierarchyMid[]
 */
const HierarchyUserCoreSchema = z.object({
  id: UuidSchema,
  fullName: z.string(),
  email: EmailSchema,
  role: UserRoleSchema,
  branch: NamedRefSchema.nullable(),
  department: NamedRefSchema.nullable(),
  isActive: z.boolean(),
});

const HierarchyLeafSchema = HierarchyUserCoreSchema.extend({
  reports: z.array(HierarchyUserCoreSchema).max(0),
});

const HierarchyMidSchema = HierarchyUserCoreSchema.extend({
  reports: z.array(HierarchyLeafSchema),
});

export const HierarchyUserNodeSchema = HierarchyUserCoreSchema.extend({
  reports: z.array(HierarchyMidSchema),
});
export type HierarchyUserNode = z.infer<typeof HierarchyUserNodeSchema>;

/// Compact node used for the "manager chain" - no `reports`. Returned
/// as an array ordered from immediate manager up to the root admin.
export const HierarchyManagerSchema = z.object({
  id: UuidSchema,
  fullName: z.string(),
  email: EmailSchema,
  role: UserRoleSchema,
});
export type HierarchyManager = z.infer<typeof HierarchyManagerSchema>;

export const UserHierarchyResponseSchema = z.object({
  user: HierarchyUserNodeSchema,
  managers: z.array(HierarchyManagerSchema),
});
export type UserHierarchyResponse = z.infer<typeof UserHierarchyResponseSchema>;

/// Email log row surfaced by `GET /users/:id/email-logs`. Used by the
/// admin user-detail page's "Email log" tab.
export const UserEmailLogItemSchema = z.object({
  id: UuidSchema,
  templateCode: z.string(),
  templateName: z.string().nullable(),
  subject: z.string(),
  toAddress: z.string(),
  status: z.enum(['PENDING', 'SENT', 'FAILED']),
  errorMessage: z.string().nullable(),
  errorAttempts: z.number().int(),
  sentAt: IsoDateTimeSchema.nullable(),
  createdAt: IsoDateTimeSchema,
});
export type UserEmailLogItem = z.infer<typeof UserEmailLogItemSchema>;

export const UserEmailLogListQuerySchema = PaginationQuerySchema.extend({
  status: z.enum(['PENDING', 'SENT', 'FAILED']).optional(),
});
export type UserEmailLogListQuery = z.infer<typeof UserEmailLogListQuerySchema>;

export const UserEmailLogListResponseSchema = paginatedResponseSchema(UserEmailLogItemSchema);
export type UserEmailLogListResponse = z.infer<typeof UserEmailLogListResponseSchema>;

/** Bulk delete: one or more user ids. The same endpoint handles a single id. */
// ----- User-level permission overrides (ADR-0039 follow-up) ----------------
//
// Two-state additive: the `userPermissions` array is the EXTRA grants
// on top of `rolePermissions` (the role baseline). The matrix UI
// shows role-granted cells locked-on; extras are operator-controlled.

export const UserPermissionsSnapshotSchema = z.object({
  userId: UuidSchema,
  rolePermissions: z.array(z.string()),
  userPermissions: z.array(z.string()),
});
export type UserPermissionsSnapshot = z.infer<typeof UserPermissionsSnapshotSchema>;

export const SetUserPermissionsRequestSchema = z.object({
  /// Operator-picked extras. Codes already covered by the role
  /// baseline are filtered server-side; pass them or not, same effect.
  codes: z.array(z.string().trim().min(1)),
});
export type SetUserPermissionsRequest = z.infer<typeof SetUserPermissionsRequestSchema>;

export const BulkDeleteRequestSchema = z.object({
  ids: z.array(UuidSchema).min(1).max(200),
});
export type BulkDeleteRequest = z.infer<typeof BulkDeleteRequestSchema>;

export const BulkDeleteResponseSchema = z.object({
  deleted: z.number().int().nonnegative().describe('How many records were soft-deleted'),
});
export type BulkDeleteResponse = z.infer<typeof BulkDeleteResponseSchema>;

// Phone shape: Indian mobile numbers. Users are Indian staff, so we
// validate against the local format rather than a loose international
// pattern. Accepts common separators (spaces / dashes / dots / parens)
// and an optional country code (+91 / 91 / 0091) or trunk-0 prefix; the
// significant number must be 10 digits starting with 6, 7, 8, or 9.
// Examples that pass: "+91-9000000001", "09000000001", "98765 43210".
const PhoneShapeSchema = z
  .string()
  .trim()
  .min(7, 'Phone must be at least 7 characters')
  .max(30, 'Phone must be at most 30 characters')
  .refine((raw) => {
    // Drop allowed formatting characters before checking the digits.
    const cleaned = raw.replace(/[\s().-]/g, '');
    // Strip the country code (+91 / 91 / 0091) or a single trunk 0,
    // then require a 10-digit mobile starting 6-9.
    return /^(?:\+?91|0091|0)?[6-9]\d{9}$/.test(cleaned);
  }, 'Enter a valid 10-digit Indian mobile number (e.g. +91 98765 43210)');

// CallerDesk member/virtual number (e.g. 09111111890). Digits with an
// optional leading + / 0; 8-15 digits. Kept lenient - these are provider-
// issued numbers, not necessarily Indian mobile format.
const CallerDeskNumberShapeSchema = z
  .string()
  .trim()
  .min(6, 'CallerDesk number looks too short')
  .max(20, 'CallerDesk number looks too long')
  .refine(
    (raw) => /^\+?\d{8,15}$/.test(raw.replace(/[\s().-]/g, '')),
    'Enter a valid CallerDesk number (digits only)',
  );

// Employee code: UTZ + DDMMYY + / + phone digits (legacy parity).
const EmployeeCodeShapeSchema = z
  .string()
  .min(2, 'Employee code must be at least 2 characters')
  .max(40, 'Employee code must be at most 40 characters')
  .regex(
    /^[A-Za-z0-9._/-]+$/,
    'Employee code may contain letters, digits, dot, underscore, hyphen, slash',
  );

export const CreateUserRequestSchema = z
  .object({
    email: EmailSchema.describe('Login email. Must be unique. Lowercased server-side.'),
    fullName: z.string().min(2).max(120).describe('Display name (2-120 chars)'),
    phone: PhoneShapeSchema.optional().describe('Optional phone number'),
    callerDeskNumber: CallerDeskNumberShapeSchema.optional().describe(
      'CallerDesk member/virtual number (unique when present). Used for click-to-call + login.',
    ),
    employeeCode: EmployeeCodeShapeSchema.optional().describe(
      'Optional HR / employee identifier (unique when present)',
    ),
    dateOfBirth: DateOnlySchema.optional().describe('Date of birth (YYYY-MM-DD)'),
    dateOfJoining: DateOnlySchema.optional().describe('Date of joining (YYYY-MM-DD)'),
    dateOfExit: DateOnlySchema.optional().describe('Date of exit (YYYY-MM-DD)'),
    isAdmin: z.boolean().optional().describe('Administrator flag'),
    role: UserRoleSchema,
    /**
     * A custom (non-system) Roles-master role to assign as the user's primary
     * role. When set, `role` is forced to READ_ONLY as a safe behavioural base
     * and this role becomes the authoritative permission grant (via user_roles).
     * Omit for a plain system role.
     */
    customRoleId: UuidSchema.nullable()
      .optional()
      .describe('Custom role UUID to assign; forces role=READ_ONLY base.'),
    branchId: UuidSchema.optional().describe('Branch UUID. Use GET /branches for valid IDs.'),
    departmentId: UuidSchema.describe('Department UUID (required). Use GET /departments.'),
    subBranchId: UuidSchema.optional().describe('Sub-branch UUID. Use GET /sub-branches.'),
    reportingManagerId: UuidSchema.optional().describe(
      'User UUID of the direct reporting manager. Required for all roles except ADMIN.',
    ),
    // 2026-06-27: `destinationCityId` removed - the ordered
    // `destinations` slots below subsume it.
    /// 2026-06-26 ADR-0057. Independent multi-selects for sales
    /// coverage. All optional - empty = "not classified".
    salesScopes: z.array(UserSalesScopeSchema).optional().describe('International / domestic'),
    autoAssignLeads: z
      .boolean()
      .optional()
      .describe('Default true. False takes the user out of round-robin lead assignment.'),
    opsDepartment: OpsDepartmentSchema.nullable()
      .optional()
      .describe('Operations desk for ops roles. Omit / null = not in any ops rotation.'),
    salesHauls: z.array(UserSalesHaulSchema).optional().describe('Short / long haul'),
    salesTripTypeIds: z
      .array(UuidSchema)
      .optional()
      .describe('TripType UUIDs (FIT / Group / Cruise / operator-added)'),
    /// Ordered destinations. priority=1 is primary; additional rows use
    /// sequential priorities (2, 3, …). Duplicate destinations or priorities
    /// are rejected.
    destinations: z
      .array(UserDestinationSlotSchema)
      .max(USER_DESTINATIONS.MAX_COUNT)
      .optional()
      .describe(
        'Ordered destination coverage. Primary (priority 1) is required when department is Sales / Domestic Sales / International Sales.',
      ),
  })
  .superRefine((v, ctx) => {
    if (v.role !== 'ADMIN' && !v.reportingManagerId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['reportingManagerId'],
        message: 'Reporting manager is required for this role',
      });
    }
    // A seller needs all four coverage dimensions, or round-robin can
    // never route a lead to them. UsersService re-checks this.
    if (isSalesRole(v.role)) {
      for (const field of missingSalesCoverage(v)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: SALES_COVERAGE_REQUIRED_MESSAGE[field],
        });
      }
    }
    if (v.destinations && v.destinations.length > 0) {
      // Slot 1 must be present when ANY destination is picked.
      if (!v.destinations.some((d) => d.priority === 1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['destinations'],
          message: 'A primary destination (priority 1) is required when any destination is picked',
        });
      }
      // Reject duplicate priorities (UNIQUE (user_id, priority) on
      // the server would 500 if we let this through).
      const seenP = new Set<number>();
      for (const d of v.destinations) {
        if (seenP.has(d.priority)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['destinations'],
            message: `Duplicate destination slot ${d.priority}`,
          });
          break;
        }
        seenP.add(d.priority);
      }
      // Reject duplicate destination IDs across slots.
      const seenD = new Set<string>();
      for (const d of v.destinations) {
        if (seenD.has(d.destinationId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['destinations'],
            message: 'Same destination picked in more than one slot',
          });
          break;
        }
        seenD.add(d.destinationId);
      }
    }
  });
export type CreateUserRequest = z.infer<typeof CreateUserRequestSchema>;

export const UpdateUserRequestSchema = z
  .object({
    fullName: z.string().min(2).max(120).optional(),
    // Allow null = clear, undefined = leave unchanged, string = validated shape.
    phone: PhoneShapeSchema.nullable()
      .optional()
      .describe('null to clear, undefined to leave unchanged'),
    callerDeskNumber: CallerDeskNumberShapeSchema.nullable()
      .optional()
      .describe('CallerDesk number: null to clear, undefined to leave unchanged'),
    employeeCode: EmployeeCodeShapeSchema.nullable()
      .optional()
      .describe('null to clear, undefined to leave unchanged'),
    dateOfBirth: DateOnlySchema.nullable()
      .optional()
      .describe('null to clear, undefined to leave unchanged'),
    dateOfJoining: DateOnlySchema.nullable()
      .optional()
      .describe('null to clear, undefined to leave unchanged'),
    dateOfExit: DateOnlySchema.nullable()
      .optional()
      .describe('null to clear, undefined to leave unchanged'),
    isAdmin: z.boolean().optional(),
    role: UserRoleSchema.optional(),
    /** Custom Roles-master role to assign as primary (forces role=READ_ONLY base). null clears it. */
    customRoleId: UuidSchema.nullable().optional(),
    branchId: UuidSchema.nullable().optional(),
    /** Department is mandatory on every user, so it can be changed but never cleared. */
    departmentId: UuidSchema.optional(),
    subBranchId: UuidSchema.nullable().optional(),
    reportingManagerId: UuidSchema.nullable()
      .optional()
      .describe(
        'Required for any role except ADMIN. Set to null to unassign manager when the new role is ADMIN.',
      ),
    // 2026-06-27: `destinationCityId` removed (see CreateUserRequest).
    /// 2026-06-26 ADR-0057. Per-user coverage dimensions. `undefined`
    /// leaves the field unchanged; any other value (incl. `[]`)
    /// overwrites it.
    salesScopes: z.array(UserSalesScopeSchema).optional(),
    autoAssignLeads: z.boolean().optional(),
    /** Operations desk. `null` clears it, `undefined` leaves it unchanged. */
    opsDepartment: OpsDepartmentSchema.nullable().optional(),
    salesHauls: z.array(UserSalesHaulSchema).optional(),
    salesTripTypeIds: z.array(UuidSchema).optional(),
    /// Ordered destinations. Same shape + rules as Create. Setting
    /// to `[]` clears the user's destinations entirely.
    destinations: z.array(UserDestinationSlotSchema).max(USER_DESTINATIONS.MAX_COUNT).optional(),
  })
  .superRefine((v, ctx) => {
    const touchesRoleOrManager = v.role !== undefined || v.reportingManagerId !== undefined;
    if (touchesRoleOrManager) {
      if (v.role !== undefined && v.role !== 'ADMIN' && !v.reportingManagerId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['reportingManagerId'],
          message: 'Reporting manager is required for this role',
        });
      }
    }
    // Same rule as create, on the fields this patch carries. A field left
    // undefined is "unchanged" and is checked server-side against what the
    // user already has.
    if (isSalesRole(v.role)) {
      for (const field of missingSalesCoverage(v)) {
        if (v[field] === undefined) continue;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [field],
          message: SALES_COVERAGE_REQUIRED_MESSAGE[field],
        });
      }
    }
    if (v.destinations && v.destinations.length > 0) {
      if (!v.destinations.some((d) => d.priority === 1)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['destinations'],
          message: 'A primary destination (priority 1) is required when any destination is picked',
        });
      }
      const seenP = new Set<number>();
      for (const d of v.destinations) {
        if (seenP.has(d.priority)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['destinations'],
            message: `Duplicate destination slot ${d.priority}`,
          });
          break;
        }
        seenP.add(d.priority);
      }
      const seenD = new Set<string>();
      for (const d of v.destinations) {
        if (seenD.has(d.destinationId)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['destinations'],
            message: 'Same destination picked in more than one slot',
          });
          break;
        }
        seenD.add(d.destinationId);
      }
    }
  });
export type UpdateUserRequest = z.infer<typeof UpdateUserRequestSchema>;
